import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import Header from "../../components/Header/Header";
import "./MainPage.css";
import {gql, useMutation} from "@apollo/client";

// -- Assets
import fondoAI from '../../assets/fondo-ai.png';
import noCodeIcon from "../../assets/code-icon.webp";
import securityIcon from "../../assets/shield-icon.webp";
import deployIcon from "../../assets/deploy-icon.webp";
import cardBackground from "../../assets/background-icon.webp";
// Banner images
import banner1 from "../../assets/banner-1.png";
import banner2 from "../../assets/banner-2.png";
import banner3 from "../../assets/banner-3.png";
import banner4 from "../../assets/banner-4.PNG";
import PhoneTilt from "../../components/PhoneTilt/PhoneTilt";
import { useHeroScrollProgress } from "../../hooks/useHeroScrollProgress";
import Coin3D from '../../components/3DCoin/3dCoin.jsx';

const fadeUp   = { hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0 } };
const fadeLeft = { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, x: 0 } };
const fadeRight= { hidden: { opacity: 0, x:  50 }, visible: { opacity: 1, x: 0 } };




const JOIN_WAITLIST = gql`

  mutation JoinWaitlist($email: String!) {
    joinWaitlist(email: $email) {
      success
      message
    }
  }
`;

/* ─────────────────────────────────────
   AILabFeaturesCarousel - Now shows alternating banners
────────────────────────────────────── */
const AILabFeaturesCarousel = ({ t }) => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const banners = [banner1, banner2, banner3, banner4];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000); // Cambia cada 5 segundos

    return () => clearInterval(interval);
  }, [banners.length]);

  return (
    <div id="ai-lab-resources" className="ai-features-carousel">
      {/* Section title */}
      <motion.h2
        className="platform-title"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.2 }}
        transition={{ duration: 0.6 }}
      >
        {t("carousel.sectionTitle")}
      </motion.h2>

      {/* Alternating banners with slide animation */}
      <div className="ai-lab-banner">
        <div className="banner-container">
          <div 
            className="banner-slider"
            style={{ transform: `translateX(-${currentBanner * 25}%)` }}
          >
            {banners.map((banner, index) => (
              <div key={index} className="banner-slide">
                <img
                  src={banner}
                  alt={`AI Lab Marketing Banner ${index + 1}`}
                  className="ai-lab-banner-image"
                />
              </div>
            ))}
          </div>
        </div>
        
        {/* Indicators */}
        <div className="banner-indicators">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBanner(index)}
              className={`banner-indicator ${currentBanner === index ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [joinWaitlist, { data, loading, error }] = useMutation(JOIN_WAITLIST);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    await joinWaitlist({ variables: { email } });
    setSubmitted(true);
  };

  // Determina si ya fue enviado correctamente
  const isSuccess = !!(data && data.joinWaitlist?.success);

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
      <input
        type="email"
        className="zenit-card-input"
        placeholder="Enter your email address..."
        value={email}
        onChange={e => setEmail(e.target.value)}
        disabled={loading || submitted || isSuccess}
        required
      />
      <button
        className="zenit-card-btn waitlist"
        type="submit"
        disabled={
          loading ||
          !email ||
          submitted ||
          isSuccess
        }
      >
        {isSuccess
          ? "You're on the waitlist!"
          : loading
            ? "Joining..."
            : "Join the Waitlist"}
      </button>
      {error && <div style={{ color: "red", marginTop: 8 }}>{error.message}</div>}
      {(data && data.joinWaitlist?.message) && (
        <div style={{ color: "#38c172", marginTop: 8 }}>
          {data.joinWaitlist.message}
        </div>
      )}
    </form>
  );
}


export default function MainPage() {
  const [showArrow, setShowArrow] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const sectionId = searchParams.get("scrollTo");
    if (sectionId) {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
   
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 50;
      setShowArrow(scrollY < threshold);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);




  const [heroRef, progress] = useHeroScrollProgress();
  const animationSlowFactor = 0.20;
// Calcula el progreso de la animación, ajustando la velocidad
  const slowProgress = Math.min(1, progress / animationSlowFactor);
  const initialTextX = 0;
  const finalTextX = -260;
  const textTranslateX = initialTextX + (finalTextX - initialTextX) * slowProgress;
  const textOpacity = 1 - slowProgress;



  return (
    <div className="main-page">
      <div className="background-fixed"></div>
      <Header />

      {/* ─ Hero ─ */}
      <div className="main-body">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 0.6 }}
        >
          <h1>{t("mainPage.hero.title")}</h1>
        </motion.div>

        {showArrow && (
          <div className="scroll-indicator">
            <motion.span
              className="arrow"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        )}
      </div>

      {/* ─ AI Lab intro ─ */}
      <div id="ai-lab" className="ai-lab-section">
        <div className="ai-lab-content">
          <motion.div
            className="ai-lab-heading-container"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="ai-lab-heading">{t("mainPage.aiLab.title")}</h1>
            <p className="ai-lab-description">
              {t("aiLab.shortDescription")}
            </p>
          </motion.div>




          {/* Feature cards */}
          <motion.div
            className="ai-lab-features"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Card 1 */}
            <div className="feature-item">
              <div className="feature-card">
                <img src={cardBackground} alt="" className="card-background" />
                <img
                  src={noCodeIcon}
                  alt={t("aiLabFeatures.noCode")}
                  className="feature-icon"
                />
              </div>
              <h3>{t("aiLabFeatures.noCode")}</h3>
            </div>

            {/* Card 2 */}
            <div className="feature-item">
              <div className="feature-card">
                <img src={cardBackground} alt="" className="card-background" />
                <img
                  src={securityIcon}
                  alt={t("aiLabFeatures.secure")}
                  className="feature-icon"
                />
              </div>
              <h3>{t("aiLabFeatures.secure")}</h3>
            </div>

            {/* Card 3 */}
            <div className="feature-item">
              <div className="feature-card">
                <img src={cardBackground} alt="" className="card-background" />
                <img
                  src={deployIcon}
                  alt={t("aiLabFeatures.deploy")}
                  className="feature-icon"
                />
              </div>
              <h3>{t("aiLabFeatures.deploy")}</h3>
            </div>
          </motion.div>
        </div>
      </div>
        <section className="zenit-ai-showcase" ref={heroRef}>
  <div className="zenit-ai-showcase-content">
    <div
      className="zenit-ai-showcase-text"
       style={{
    transform: `translateX(${textTranslateX}px)`,
    opacity: Math.max(0, textOpacity),
    width: progress > 0.96 ? "0" : "auto",
    minWidth: progress > 0.96 ? "0" : "340px",
    flex: progress > 0.96 ? "0 0 0" : "1 1 0",
    transition: "all 0.6s cubic-bezier(.8,0,.2,1), opacity 0.5s",
    overflow: "hidden",
  }}
    >
      <h2 className="zenit-heading">
        See how <span className="zenit-highlight">Zenit AI Lab</span><br />
        <span className="zenit-subheading">takes your ideas further</span>
      </h2>
    </div>
    <PhoneTilt progress={progress} />
  </div>
</section>

      {/* ─ Carrusel de funcionalidades ─ */}
      <AILabFeaturesCarousel t={t} />

<section className="zenit-cards-section">

  <div className="halo-bg"></div>

 
  <h2 className="zenit-title">
    Take your automation to the next level
  </h2>


  <div className="zenit-cards-container">
 
    <div className="zenit-card">
      <div className="dots-bg" />
      <h3>Zenit Pilot Program</h3>
      <p>
        Our pilot program is designed for companies interested in contributing to the next generation of AI-driven automation. Zenit is selecting a limited number of partners for this early phase.
      </p>
<button
  className="zenit-card-btn"
  onClick={() =>
    window.location.href = `/contact?reason=${encodeURIComponent('Consult Pilot plan')}`
  }
>
  {t("Consult Pilot plan")}
  <span className="zenit-arrow">→</span>
</button>

    </div>

    {/* CARD 2 */}
    <div className="zenit-card">
      <div className="dots-bg" />
      <h3>
        Coming soon: <span className="zenit-lab">Zenit <span className="ai-lab">AI Lab</span></span>
      </h3>
      <p>
        We’re almost ready to launch. Join our waitlist and we’ll let you know when Zenit is ready.
      </p>
      <WaitlistForm />

    </div>
  </div>
</section>





{/* ——— Sección AI Specialized ——— */}
<section
  id="ai-specialized"
  className="ai-specialized-section"
>
  {/* — Título principal */}
  <motion.h1
    className="ai-specialized-title"
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: false, amount: 0.2 }}
    transition={{ duration: 0.6 }}
  >
    From workflows to <span className="bold">solutions made just for you</span>
  </motion.h1>

  {/* — Contenido: lista + imagen */}
  <div className="ai-specialized-content">
    <motion.ul
      className="ai-specialized-list"
      variants={fadeLeft}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.2 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <li>When speed is critical, but your processes are complex.</li>
      <li>When resources are tight, or existing tools fall short.</li>
      <li>Your processes, data, goals, and challenges are unique.</li>
      <li>Workflows drive your automation,</li>
      <li>but what you need is a tailored solution.</li>
    </motion.ul>

    <motion.div
      className="ai-specialized-image"
      variants={fadeRight}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.2 }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      <img src={fondoAI} alt="AI puzzle chip" />
    </motion.div>
  </div>

  {/* — Tagline + descripción */}
  <motion.div
    className="ai-specialized-footer"
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: false, amount: 0.2 }}
    transition={{ duration: 0.6, delay: 0.4 }}
  >
    <p className="ai-specialized-tagline">
      That's where <span className="highlight">ZENIT AI LAB</span> come in
    </p>
    <p className="ai-specialized-description">
      Zenit AI Lab offers a premium B2B service: enterprise-grade AI solutions
      designed to transform challenges into competitive advantages through
      intelligent automation.
    </p>
  </motion.div>

  {/* — CTA */}
  <motion.div
    className="ai-specialized-cta"
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: false, amount: 0.2 }}
    transition={{ duration: 0.6, delay: 0.5 }}
  >
    <button
      href="/contact?reason=Specialized AI Solutions"
      className="ai-specialized-cta-button"
    >
      Reach out to discuss your project
    </button>
  </motion.div>
</section>
{/* —————————————————————————————— */}




{/* ─────────── ZENIT COINS ─────────── */}
<div id="zenit-coins" className="zenit-coins-section">
  <div className="zenit-coins-background" />

  {/* 3D al centro (flotante) */}
  <div className="zenit-coins-spline">
    <Coin3D />
  </div>

  {/* Columna izquierda: título + tagline */}
  <motion.div
    className="zenit-coins-left"
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
  >
    <h1 className="zenit-coins-title">
      {t("mainPage.zenitCoins.title")}
    </h1>
    <p className="zenit-coins-tagline">
      {t("mainPage.zenitCoins.description")}
    </p>
  </motion.div>

  {/* Columna derecha: párrafo grande */}
  <motion.div
    className="coin-description"
    variants={fadeRight}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: 0.3 }}
  >
    <h3>{t("mainPage.zenitCoins.ctaTitle")}</h3>
  </motion.div>

{/* Botón */}
<a
  href="/Contact?reason=Coins"
  className="zenit-coins-button"
>
  {t("mainPage.zenitCoins.buttonText")}
</a>

</div>


    </div>
  );
}